"use client"

import { useEffect, useRef, useState, type CSSProperties } from "react"
import Image from "next/image"
import { useTranslations } from "next-intl"
import { ChevronLeftIcon, ChevronRightIcon } from "@/components/ui/icons"
import type { QuestionCard } from "@/components/ui/questions-help-section"
import { blobMediaUrl } from "@/lib/blob-url"

// Gradually stack masked blur layers, keeping the centre of each photo clear.
// The incremental radii keep the combined blur gentle at the answer's edge.
const answerBlur: CSSProperties[] = Array.from({ length: 5 }, (_, index) => {
  const radius = Math.sqrt((index + 1) ** 2 - index ** 2) * 1.4
  const mask = `linear-gradient(to bottom, transparent ${68 + index * 6}%, black ${74 + index * 6}%)`
  return {
    backdropFilter: `blur(${radius}px)`,
    WebkitBackdropFilter: `blur(${radius}px)`,
    maskImage: mask,
    WebkitMaskImage: mask,
  }
})

function LinkedExplanation({ question }: { question: QuestionCard }) {
  const { explanation, articleHref, articleLinkText } = question
  if (!articleHref || !articleLinkText || typeof explanation !== "string")
    return <>{explanation}</>

  const start = explanation.indexOf(articleLinkText)
  if (start === -1) return <>{explanation}</>

  return (
    <>
      {explanation.slice(0, start)}
      <a
        href={articleHref}
        className="rounded-sm text-white/90 underline decoration-white/40 underline-offset-4 transition-colors hover:text-white hover:decoration-white focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-white"
      >
        {articleLinkText}
      </a>
      {explanation.slice(start + articleLinkText.length)}
    </>
  )
}

export function QuestionsHelpCarousel({
  heading,
  description,
  label,
  questions,
}: {
  heading: string
  description?: string
  label?: string
  questions: QuestionCard[]
}) {
  const ui = useTranslations("ui")
  const track = useRef<HTMLDivElement>(null)
  const [position, setPosition] = useState({
    previous: false,
    next: false,
  })

  useEffect(() => {
    const element = track.current
    if (!element) return
    const update = () => {
      setPosition({
        previous: element.scrollLeft > 2,
        next:
          element.scrollLeft < element.scrollWidth - element.clientWidth - 2,
      })
    }
    update()
    const resize = new ResizeObserver(update)
    resize.observe(element)
    element.addEventListener("scroll", update, { passive: true })
    return () => {
      resize.disconnect()
      element.removeEventListener("scroll", update)
    }
  }, [questions.length])

  const move = (direction: number) => {
    const element = track.current
    if (!element) return
    const first = element.firstElementChild as HTMLElement | null
    const step =
      (first?.offsetWidth ?? 340) + parseFloat(getComputedStyle(element).gap)
    element.scrollBy({
      left: step * direction,
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
        ? "instant"
        : "smooth",
    })
  }

  return (
    <section
      id="questions"
      aria-labelledby="questions-heading"
      className="overflow-x-clip bg-neutral-50 py-16 md:py-20"
    >
      <div className="mx-auto mb-8 flex max-w-[1440px] items-end justify-between gap-8 px-4 md:px-10">
        <div>
          {label && (
            <p className="type-label-lg mb-3 text-neutral-500 uppercase">
              {label}
            </p>
          )}
          <h2
            id="questions-heading"
            className="type-display-md max-w-[650px] text-neutral-900"
          >
            {heading}
          </h2>
          {description && (
            <p className="type-body-lg mt-4 max-w-[640px] text-neutral-600">
              {description}
            </p>
          )}
        </div>
      </div>
      <div
        id="questions-track"
        ref={track}
        tabIndex={0}
        aria-label={heading}
        onKeyDown={(event) => {
          if (
            event.target === event.currentTarget &&
            (event.key === "ArrowLeft" || event.key === "ArrowRight")
          ) {
            event.preventDefault()
            move(event.key === "ArrowLeft" ? -1 : 1)
          }
        }}
        className="flex snap-x snap-mandatory [scroll-padding-inline:1rem] [scrollbar-width:none] gap-3 overflow-x-auto px-4 pb-1 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-neutral-900 md:[scroll-padding-inline:max(2.5rem,calc((100vw-1360px)/2))] md:gap-4 md:px-[max(2.5rem,calc((100vw-1360px)/2))] [&::-webkit-scrollbar]:hidden"
      >
        {questions.map((question) => (
          <article
            key={question.quote}
            className="relative isolate h-[500px] w-[calc(100vw-56px)] max-w-[340px] shrink-0 snap-start overflow-hidden rounded-[20px] bg-neutral-700 text-white md:w-[340px]"
          >
            <Image
              src={blobMediaUrl(question.photo)}
              alt=""
              fill
              sizes="(max-width: 767px) calc(100vw - 56px), 340px"
              className="object-cover"
              style={
                question.photoObjectPosition
                  ? { objectPosition: question.photoObjectPosition }
                  : undefined
              }
            />
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0"
            >
              {answerBlur.map((style, layer) => (
                <div key={layer} className="absolute inset-0" style={style} />
              ))}
              <div className="absolute inset-0 bg-gradient-to-b from-neutral-950/60 via-neutral-950/0 via-45% to-neutral-950/90" />
            </div>
            <div className="relative z-10 flex h-full flex-col p-6">
              <h3 className="type-headline-sm">{question.quote}</h3>
              <p className="type-body-xl-emphasized mt-auto pt-8 text-white/90">
                <LinkedExplanation question={question} />
              </p>
            </div>
          </article>
        ))}
      </div>
      <div className="mx-auto mt-6 flex max-w-[1440px] justify-end gap-2 px-4 md:px-10">
        {[-1, 1].map((direction) => (
          <button
            key={direction}
            type="button"
            aria-label={ui(direction === -1 ? "scrollLeft" : "scrollRight")}
            aria-controls="questions-track"
            disabled={direction === -1 ? !position.previous : !position.next}
            onClick={() => move(direction)}
            className="flex size-12 cursor-pointer items-center justify-center rounded-full bg-white text-neutral-900 transition-colors hover:bg-neutral-100 focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-neutral-900 disabled:cursor-default disabled:text-neutral-300 disabled:hover:bg-white"
          >
            {direction === -1 ? (
              <ChevronLeftIcon width={24} height={24} />
            ) : (
              <ChevronRightIcon width={24} height={24} />
            )}
          </button>
        ))}
      </div>
    </section>
  )
}
